import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Plus, Bell, Home, FileText, Settings, LogOut, 
  AlertTriangle, Clock, CheckCircle, ChevronRight,
  Flame, Droplets, Zap, Refrigerator, Cloud, Lock, MoreHorizontal,
  Shield, Building2, User, Phone, Mail, Calendar, PoundSterling,
  MessageSquare, Download, MapPin, FileCheck, CreditCard
} from "lucide-react";
import { API } from "@/App";

const issueTypeIcons = {
  Heating: Flame,
  Plumbing: Droplets,
  Electrical: Zap,
  Appliance: Refrigerator,
  Damp: Cloud,
  Locks: Lock,
  Other: MoreHorizontal
};

const statusConfig = {
  reported: { label: "Reported", color: "bg-blue-100 text-blue-700", icon: FileText },
  in_progress: { label: "In Progress", color: "bg-amber-100 text-amber-700", icon: Clock },
  escalated: { label: "Escalated", color: "bg-orange-100 text-orange-700", icon: AlertTriangle },
  contractor_assigned: { label: "Contractor Assigned", color: "bg-purple-100 text-purple-700", icon: User },
  scheduled: { label: "Scheduled", color: "bg-indigo-100 text-indigo-700", icon: Calendar },
  quote_pending: { label: "Quote Pending", color: "bg-yellow-100 text-yellow-700", icon: PoundSterling },
  approved: { label: "Approved", color: "bg-green-100 text-green-700", icon: CheckCircle },
  completed: { label: "Completed", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
  resolved: { label: "Resolved", color: "bg-green-100 text-green-700", icon: CheckCircle }
};

export const TenantDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tenancy, setTenancy] = useState(null);
  const [property, setProperty] = useState(null);
  const [landlord, setLandlord] = useState(null);
  const [issues, setIssues] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [rentHistory, setRentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("home");

  const token = localStorage.getItem("token");

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [issuesRes, jobsRes, notifsRes, tenancyRes] = await Promise.all([
        axios.get(`${API}/issues`, { headers }),
        axios.get(`${API}/jobs`, { headers }),
        axios.get(`${API}/notifications`, { headers }),
        axios.get(`${API}/lettings/tenant/my-tenancy`, { headers }).catch(() => ({ data: null }))
      ]);

      setIssues(issuesRes.data);
      setJobs(jobsRes.data);
      setNotifications(notifsRes.data.filter(n => !n.is_read));
      
      if (tenancyRes.data) {
        setTenancy(tenancyRes.data.tenancy);
        setProperty(tenancyRes.data.property);
        setLandlord(tenancyRes.data.landlord);
        setDocuments(tenancyRes.data.documents || []);
        setRentHistory(tenancyRes.data.rent_history || []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    toast.success("Logged out successfully");
    navigate("/login");
  };

  const unreadCount = notifications.length;
  const activeIssues = issues.filter(i => !['resolved', 'completed'].includes(i.status));
  const scheduledJobs = jobs.filter(j => j.status === 'scheduled');

  // Calculate next rent due
  const getNextRentDue = () => {
    if (!tenancy?.rent_amount) return null;
    const today = new Date();
    const dueDay = tenancy.rent_due_day || 1;
    let nextDue = new Date(today.getFullYear(), today.getMonth(), dueDay);
    if (nextDue <= today) {
      nextDue = new Date(today.getFullYear(), today.getMonth() + 1, dueDay);
    }
    return nextDue;
  };

  const nextRentDue = getNextRentDue();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pb-24">
      {/* Header with logo */}
      <header className="bg-black border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="/goodacres-full-logo.jpg" 
              alt="Goodacres" 
              className="h-10 w-auto"
            />
            <div>
              <p className="text-sm text-white/50">Welcome back,</p>
              <h1 className="text-xl font-semibold text-white">{user?.name || "Tenant"}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="notifications-btn"
              variant="ghost"
              size="icon"
              className="relative text-white/70 hover:text-white hover:bg-white/10"
              onClick={() => setActiveTab("notifications")}
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </Button>
            <Button
              data-testid="logout-btn"
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 mb-6">
            <TabsTrigger value="home" className="text-xs">
              <Home className="w-4 h-4 mr-1" /> Home
            </TabsTrigger>
            <TabsTrigger value="tenancy" className="text-xs">
              <Building2 className="w-4 h-4 mr-1" /> Tenancy
            </TabsTrigger>
            <TabsTrigger value="documents" className="text-xs">
              <FileText className="w-4 h-4 mr-1" /> Docs
            </TabsTrigger>
            <TabsTrigger value="rent" className="text-xs">
              <PoundSterling className="w-4 h-4 mr-1" /> Rent
            </TabsTrigger>
          </TabsList>

          {/* HOME TAB */}
          <TabsContent value="home" className="space-y-6">
            {/* Property Info Card */}
            {property && (
              <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Home className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-800">{property.address || property.address_line1}</p>
                      <p className="text-sm text-slate-500">{property.postcode}</p>
                      {tenancy?.rent_amount && (
                        <p className="text-lg font-bold text-blue-600 mt-1">
                          £{tenancy.rent_amount.toLocaleString()} /month
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-3">
              <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab("home")}>
                <CardContent className="p-3 text-center">
                  <p className="text-2xl font-bold text-amber-600">{activeIssues.length}</p>
                  <p className="text-xs text-muted-foreground">Active Issues</p>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:shadow-md transition-shadow">
                <CardContent className="p-3 text-center">
                  <p className="text-2xl font-bold text-blue-600">{scheduledJobs.length}</p>
                  <p className="text-xs text-muted-foreground">Scheduled</p>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab("documents")}>
                <CardContent className="p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{documents.length}</p>
                  <p className="text-xs text-muted-foreground">Documents</p>
                </CardContent>
              </Card>
            </div>

            {/* Report Issue Card */}
            <Card 
              data-testid="report-issue-card"
              className="bg-gradient-to-br from-primary to-blue-600 text-white border-0 cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => navigate("/tenant/report")}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold mb-1">Report an Issue</h2>
                    <p className="text-blue-100 text-sm">Get help with maintenance problems</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                    <Plus className="w-6 h-6" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* My Documents Quick Link */}
            <Card 
              className="border-emerald-200 bg-emerald-50 cursor-pointer hover:bg-emerald-100 transition-colors"
              onClick={() => navigate("/my-documents")}
              data-testid="my-documents-card"
            >
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium">My Documents</p>
                    <p className="text-sm text-muted-foreground">Upload ID for verification</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-emerald-600" />
              </CardContent>
            </Card>

            {/* Active Issues */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold font-outfit">Your Issues</h2>
                {issues.length > 0 && (
                  <Badge variant="secondary">{issues.length}</Badge>
                )}
              </div>

              {issues.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="py-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="font-medium text-slate-700 mb-1">No issues reported</h3>
                    <p className="text-sm text-muted-foreground">
                      Everything looks good! Report a new issue if something needs fixing.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {issues.slice(0, 5).map((issue) => {
                    const IconComponent = issueTypeIcons[issue.issue_type] || MoreHorizontal;
                    const status = statusConfig[issue.status] || statusConfig.reported;

                    return (
                      <Card
                        key={issue.issue_id}
                        data-testid={`issue-card-${issue.issue_id}`}
                        className="cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => navigate(`/tenant/issue/${issue.issue_id}/chat`)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                              <IconComponent className="w-5 h-5 text-slate-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-medium truncate">{issue.issue_type}</h3>
                                <Badge className={status.color}>{status.label}</Badge>
                              </div>
                              <p className="text-sm text-muted-foreground line-clamp-2">
                                {issue.description}
                              </p>
                              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                <span>{new Date(issue.created_at).toLocaleDateString()}</span>
                                {issue.contractor_name && (
                                  <span className="flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    {issue.contractor_name}
                                  </span>
                                )}
                              </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" />
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Scheduled Jobs */}
            {jobs.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold font-outfit">Scheduled Visits</h2>
                  <Badge variant="secondary">{jobs.length}</Badge>
                </div>

                <div className="space-y-3">
                  {jobs.slice(0, 3).map((job) => (
                    <Card
                      key={job.job_id}
                      data-testid={`job-card-${job.job_id}`}
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => navigate(`/tenant/job/${job.job_id}`)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={statusConfig[job.status]?.color || "bg-slate-100"}>
                                {job.status?.replace(/_/g, " ")}
                              </Badge>
                              <span className="text-sm text-muted-foreground">
                                #{job.job_id?.slice(-6)}
                              </span>
                            </div>
                            <p className="font-medium">{job.issue_type || "Maintenance"}</p>
                            {job.contractor_name && (
                              <p className="text-sm text-muted-foreground">
                                Contractor: {job.contractor_name}
                              </p>
                            )}
                            {job.scheduled_time && (
                              <p className="text-sm text-primary font-medium mt-1">
                                <Calendar className="w-3 h-3 inline mr-1" />
                                {new Date(job.scheduled_time).toLocaleString()}
                              </p>
                            )}
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>
            )}
          </TabsContent>

          {/* TENANCY TAB */}
          <TabsContent value="tenancy" className="space-y-6">
            {/* Property Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Home className="w-5 h-5 text-blue-600" />
                  Property Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {property ? (
                  <>
                    <div>
                      <p className="text-sm text-muted-foreground">Address</p>
                      <p className="font-medium">{property.address || property.address_line1}</p>
                      <p className="text-sm">{property.postcode}</p>
                    </div>
                    <div className="grid grid-cols-3 gap-4 pt-3 border-t">
                      <div>
                        <p className="text-sm text-muted-foreground">Bedrooms</p>
                        <p className="font-medium">{property.bedrooms || "-"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Bathrooms</p>
                        <p className="font-medium">{property.bathrooms || "-"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Type</p>
                        <p className="font-medium capitalize">{property.property_type || "-"}</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-muted-foreground text-center py-4">No property linked</p>
                )}
              </CardContent>
            </Card>

            {/* Tenancy Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="w-5 h-5 text-purple-600" />
                  Tenancy Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {tenancy ? (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Start Date</p>
                        <p className="font-medium">{tenancy.tenancy_start || tenancy.start_date || "-"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">End Date</p>
                        <p className="font-medium">{tenancy.tenancy_end || tenancy.end_date || "Periodic"}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-3 border-t">
                      <div>
                        <p className="text-sm text-muted-foreground">Monthly Rent</p>
                        <p className="font-bold text-lg text-emerald-600">
                          £{(tenancy.rent_amount || 0).toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Deposit</p>
                        <p className="font-medium">£{(tenancy.deposit || tenancy.deposit_amount || 0).toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="pt-3 border-t">
                      <p className="text-sm text-muted-foreground">Rent Due</p>
                      <p className="font-medium">{tenancy.rent_due_day || 1}st of each month</p>
                    </div>
                  </>
                ) : (
                  <p className="text-muted-foreground text-center py-4">No tenancy details</p>
                )}
              </CardContent>
            </Card>

            {/* Landlord / Agency Contact */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-amber-600" />
                  Contact
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Agency */}
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600 font-medium mb-2">MANAGING AGENT</p>
                  <p className="font-medium">Goodacres Residential</p>
                  <div className="flex items-center gap-4 mt-2">
                    <Button size="sm" variant="outline" className="flex-1" asChild>
                      <a href="tel:01onal234567890">
                        <Phone className="w-4 h-4 mr-1" /> Call
                      </a>
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1" asChild>
                      <a href="mailto:lettings@goodacres.com">
                        <Mail className="w-4 h-4 mr-1" /> Email
                      </a>
                    </Button>
                  </div>
                </div>

                {/* Landlord (if visible) */}
                {landlord && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-600 font-medium mb-2">LANDLORD</p>
                    <p className="font-medium">{landlord.name}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* DOCUMENTS TAB */}
          <TabsContent value="documents" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Tenancy Documents
                </CardTitle>
                <CardDescription>Important documents for your tenancy</CardDescription>
              </CardHeader>
              <CardContent>
                {documents.length > 0 ? (
                  <div className="space-y-3">
                    {documents.map((doc, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <FileText className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">{doc.name}</p>
                            <p className="text-xs text-muted-foreground">{doc.type}</p>
                          </div>
                        </div>
                        <Button size="sm" variant="ghost">
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No documents available</p>
                    <p className="text-sm mt-1">Documents will appear here when uploaded by your agent</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Compliance Documents */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-emerald-600" />
                  Compliance Certificates
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  <div className={`p-3 rounded-lg text-center ${property?.epc_rating_current ? 'bg-green-50' : 'bg-slate-50'}`}>
                    <Zap className="w-6 h-6 mx-auto mb-1 text-amber-500" />
                    <p className="text-xs font-medium">EPC</p>
                    <Badge variant={property?.epc_rating_current ? "default" : "secondary"} className="mt-1">
                      {property?.epc_rating_current || "N/A"}
                    </Badge>
                  </div>
                  <div className={`p-3 rounded-lg text-center ${property?.gas_safety_expiry ? 'bg-green-50' : 'bg-slate-50'}`}>
                    <Flame className="w-6 h-6 mx-auto mb-1 text-red-400" />
                    <p className="text-xs font-medium">Gas Safe</p>
                    <Badge variant={property?.gas_safety_expiry ? "default" : "secondary"} className="mt-1">
                      {property?.gas_safety_expiry ? "Valid" : "N/A"}
                    </Badge>
                  </div>
                  <div className={`p-3 rounded-lg text-center ${property?.eicr_expiry ? 'bg-green-50' : 'bg-slate-50'}`}>
                    <Zap className="w-6 h-6 mx-auto mb-1 text-blue-500" />
                    <p className="text-xs font-medium">EICR</p>
                    <Badge variant={property?.eicr_expiry ? "default" : "secondary"} className="mt-1">
                      {property?.eicr_expiry ? "Valid" : "N/A"}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* RENT TAB */}
          <TabsContent value="rent" className="space-y-6">
            {/* Next Payment Due */}
            {tenancy?.rent_amount && (
              <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Next Payment Due</p>
                      <p className="text-3xl font-bold text-emerald-600">
                        £{tenancy.rent_amount.toLocaleString()}
                      </p>
                      {nextRentDue && (
                        <p className="text-sm text-slate-600 mt-1">
                          Due: {nextRentDue.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                    <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                      <PoundSterling className="w-8 h-8 text-emerald-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Payment Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                  Payment Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-blue-800 mb-2">Bank Details for Rent Payment</p>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-muted-foreground">Account Name:</span> Goodacres Client Account</p>
                    <p><span className="text-muted-foreground">Sort Code:</span> 00-00-00</p>
                    <p><span className="text-muted-foreground">Account Number:</span> 00000000</p>
                    <p><span className="text-muted-foreground">Reference:</span> {tenancy?.reference || user?.name || "Your Name"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment History */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-slate-600" />
                  Payment History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {rentHistory.length > 0 ? (
                  <div className="space-y-3">
                    {rentHistory.map((payment, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 border-b last:border-0"
                      >
                        <div>
                          <p className="font-medium">
                            {new Date(payment.date_received || payment.date).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                          </p>
                          <p className="text-xs text-muted-foreground capitalize">
                            {payment.payment_method?.replace('_', ' ') || 'Bank Transfer'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-emerald-600">£{(payment.amount || 0).toLocaleString()}</p>
                          <Badge className="bg-green-100 text-green-700">Paid</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No payment history</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-50">
        <div className="max-w-2xl mx-auto flex items-center justify-around py-2">
          <Button 
            variant="ghost" 
            className={`flex-col gap-1 h-auto py-2 ${activeTab === 'home' ? 'text-primary' : ''}`}
            onClick={() => setActiveTab("home")}
          >
            <Home className="w-5 h-5" />
            <span className="text-xs">Home</span>
          </Button>
          <Button 
            variant="ghost" 
            className="flex-col gap-1 h-auto py-2"
            onClick={() => navigate("/tenant/report")}
          >
            <Plus className="w-5 h-5" />
            <span className="text-xs">Report</span>
          </Button>
          <Button 
            variant="ghost" 
            className="flex-col gap-1 h-auto py-2 relative"
            onClick={() => setActiveTab("notifications")}
          >
            <Bell className="w-5 h-5" />
            <span className="text-xs">Alerts</span>
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1/4 w-2 h-2 bg-red-500 rounded-full"></span>
            )}
          </Button>
          <Button 
            variant="ghost" 
            className="flex-col gap-1 h-auto py-2"
            onClick={() => setActiveTab("tenancy")}
          >
            <MessageSquare className="w-5 h-5" />
            <span className="text-xs">Contact</span>
          </Button>
        </div>
      </nav>
    </div>
  );
};
